"use client";

import { ArrowRight, Play } from "lucide-react";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FadeInUp } from "@/components/ui/motion";
import { trackLeadSignup, trackViewDemo } from "@/lib/analytics";
import { ProductTour } from "./ProductTour";
import { WorkflowDemo } from "./WorkflowDemo";

export function Hero() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const navigate = useRouter();

  return (
    <section className="relative pt-32 sm:pt-32 lg:pt-48 pb-16 sm:pb-20 lg:pb-28 overflow-hidden">
      {/* Animated Background - Floating Gradient Orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Primary orb - slow floating */}
        <motion.div
          className="absolute top-10 left-1/4 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-primary/20 rounded-full blur-[80px] sm:blur-[140px] animate-gradient-shift"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        />
        {/* Secondary orb - reverse float */}
        <motion.div
          className="absolute top-32 right-1/4 w-[250px] sm:w-[500px] h-[250px] sm:h-[500px] bg-accent/15 rounded-full blur-[70px] sm:blur-[120px] animate-float-slow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
        />
        {/* Accent orb - bottom */}
        <motion.div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[400px] sm:w-[800px] h-[200px] sm:h-[400px] bg-primary/10 rounded-full blur-[100px] sm:blur-[160px] animate-float-reverse"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
        />
        {/* Subtle grid pattern overlay */}
        <div className="absolute inset-0 pattern-grid opacity-30" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          {/* Enhanced Badge with shimmer */}
          <FadeInUp>
            <div className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 text-[10px] sm:text-xs font-medium text-primary mb-6 overflow-hidden">
              <span className="absolute inset-0 animate-shimmer" />
              <span className="relative flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                Now in public beta
              </span>
            </div>
          </FadeInUp>

          {/* Headline */}
          <FadeInUp delay={0.1}>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl text-foreground leading-[1.1] sm:leading-[1.08] mb-6 tracking-tight">
              Your Life and Business, <br />
              <span className="gradient-text">on Autopilot</span>
            </h1>
          </FadeInUp>

          {/* Subheadline */}
          <FadeInUp delay={0.2}>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground/90 max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed">
              From simple daily tasks to complex business logic, Makeplans is the
              beautiful, reliable way to connect your apps and automate your
              world, without the headache.
            </p>
          </FadeInUp>

          {/* CTA Row */}
          <FadeInUp delay={0.3}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-6">
              <Button
                // variant="gradient"
                size="lg"
                onClick={() => {
                  trackLeadSignup("landing_hero_primary");
                  navigate.push("/register");
                }}
                className="pulse-cta w-full sm:w-auto"
              >
                Start building free
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  trackViewDemo("landing_hero");
                  setIsVideoOpen(true);
                }}
                className="w-full sm:w-auto"
              >
                <Play className="w-4 h-4" />
                Watch demo
                <span className="text-xs text-muted-foreground">(25s)</span>
              </Button>
            </div>
          </FadeInUp>

          {/* Microcopy */}
          <FadeInUp delay={0.4}>
            <p className="text-sm text-muted-foreground mb-8">
              Free tier available · No credit card to start
            </p>
          </FadeInUp>
        </div>

        {/* Hero Visual with 3D Perspective */}
        <FadeInUp delay={0.5}>
          <div
            className="relative max-w-5xl mx-auto"
            style={{ perspective: "1200px" }}
          >
            {/* Multi-layer glow effect */}
            <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-b from-primary/20 via-primary/5 to-transparent rounded-3xl blur-2xl" />
            <div className="absolute -inset-4 sm:-inset-8 bg-primary/5 rounded-3xl blur-3xl animate-gradient-shift" />

            {/* Floating metrics - left */}
            {/* <motion.div 
              className="hidden lg:flex absolute -left-4 top-1/4 z-20"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
            >
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card/90 border border-border shadow-lg backdrop-blur-sm animate-float">
                <Zap className="w-4 h-4 text-warning" />
                <div className="text-xs">
                  <div className="font-semibold text-foreground">1.2M</div>
                  <div className="text-muted-foreground">events/day</div>
                </div>
              </div>
            </motion.div> */}

            {/* Floating metrics - right */}
            {/* <motion.div 
              className="hidden lg:flex absolute -right-4 top-1/3 z-20"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.4 }}
            >
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card/90 border border-border shadow-lg backdrop-blur-sm animate-float" style={{ animationDelay: "1s" }}>
                <TrendingUp className="w-4 h-4 text-success" />
                <div className="text-xs">
                  <div className="font-semibold text-foreground">99.9%</div>
                  <div className="text-muted-foreground">uptime</div>
                </div>
              </div>
            </motion.div> */}

            {/* Floating metrics - bottom left */}
            {/* <motion.div 
              className="hidden lg:flex absolute -left-8 bottom-1/4 z-20"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.6 }}
            >
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card/90 border border-border shadow-lg backdrop-blur-sm animate-float" style={{ animationDelay: "2s" }}>
                <Shield className="w-4 h-4 text-primary" />
                <div className="text-xs">
                  <div className="font-semibold text-foreground">SOC 2</div>
                  <div className="text-muted-foreground">compliant</div>
                </div>
              </div>
            </motion.div> */}

            {/* Product frame with 3D tilt */}
            <motion.div
              className="relative bg-card rounded-xl border border-border shadow-2xl overflow-hidden"
              initial={{ opacity: 0, y: 40, rotateX: 8 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* Top browser bar for realism */}
              <div className="flex items-center gap-2 px-4 py-3 bg-surface border-b border-border">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-destructive/60" />
                  <div className="w-3 h-3 rounded-full bg-warning/60" />
                  <div className="w-3 h-3 rounded-full bg-success/60" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="px-4 py-1 rounded-md bg-muted/50 text-xs text-muted-foreground">
                    app.makeplans.tech
                  </div>
                </div>
              </div>

              <WorkflowDemo />
            </motion.div>

            {/* Feature callouts */}
            <motion.div
              className="absolute -bottom-4 left-1/2 -translate-x-1/2 z-10"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.2 }}
            >
              <div className="flex items-center gap-3 px-4 py-2.5 rounded-full bg-card/95 border border-border shadow-xl backdrop-blur-sm text-xs">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                  <span className="text-foreground font-medium">
                    Auto-retries
                  </span>
                </span>
                <span className="w-px h-4 bg-border" />
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-foreground font-medium">
                    Real-time logs
                  </span>
                </span>
                <span className="w-px h-4 bg-border" />
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-warning" />
                  <span className="text-foreground font-medium">
                    Instant replay
                  </span>
                </span>
              </div>
            </motion.div>
          </div>
        </FadeInUp>
      </div>

      {/* Product Tour */}
      <ProductTour open={isVideoOpen} onOpenChange={setIsVideoOpen} />
    </section>
  );
}
